/* eslint-disable no-confusing-arrow */
import moment from 'moment';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import {
  LinearProgress,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
  ITeamData,
  IProps,
  IValueOption,
  IScheduleOption,
  mapStateToProps,
  mapDispatchToProps,
} from './interfaces';
import {
  ProgressContainer,
  Form, FormGroup, FormRow,
  StyledTextField, StyledDatePicker,
  Select, Label,
} from './styles';
import { DialogContentContainer } from '../../Shared/styles/dialog';
import { WarningContainer } from '../../Shared/Warning';
import {
  canUploadNewGame,
  isLacrosse,
  isSoccer,
  newerCalendersFirst,
  useCalendarSplit,
} from '../../../utils/exchange';
import { VideoCodec } from '../../../enums';
import { EXCHANGE_COMMENT_MAX_LENGTH } from '../../../constants';
import {
  teamToOption, FormType, ITeamOption, newGameSeasons,
} from '../../../state/exchange/gameScheduleForm';
import { XML_QUALIFIER } from '../../../state/exchange/uploadDialog';
import { ILookupOption } from '../../../state/lookups';
import { getGameId } from '../../../api/vault/exchangeSchedule';
import { parseDatefromString } from '../../../utils/time';
import {
  valueToOption, valuesToOptions,
  scheduleToOption, schedulesToOptions, lookupToOption,
  TeamOption, GameOption, GameIdField, VirtualizedMenuList,
} from './utils';
import Filters from './filters';

const defaultGameType = 'Regular Season';

class GameScheduleForm extends PureComponent<IProps> {
  static defaultProps = {
    isSeasonSelectable: true,
    isGameMode: false,
    isCreateMode: false,
    isUpdateMode: false,
    isAssetUpdateMode: false,
    isUploadMode: false,
    isChangeGameMode: false,
  };

  isUpdateMode = this.props.formType === FormType.Update;
  isCreateMode = this.props.formType === FormType.Create;
  isUploadMode = this.props.formType === FormType.Upload;
  isUpdateAssetsMode = this.props.formType === FormType.UpdateAssets;

  // A getter, not a class field: the sport can change mid-session now that the game
  // being uploaded for drives it, so eligibility has to be re-evaluated on each render
  // rather than frozen at construction. Rule itself is unchanged.
  get allowNewGame(): boolean {
    return this.isUploadMode && canUploadNewGame(this.props.sport);
  }

  checkNewGameSelected = () => {
    const { selectedSchedule } = this.props;
    return (selectedSchedule && !selectedSchedule._id);
  };

  componentDidMount = () => {
    const {
      onQueryExchangeSchedules,
      onQueryTeamsForCreate,
      sport,
      productSport,
      onUpdateQualifierLookup,
    } = this.props;
    if (!this.isCreateMode) onQueryExchangeSchedules(false, this.allowNewGame, this.isUploadMode);
    else onQueryTeamsForCreate();
    // Lookups were loaded for the user's own sport at app init. If we open on a game
    // from another sport (e.g. a prefilled last upload), its qualifiers differ.
    if (this.isUploadMode && sport && sport !== productSport) onUpdateQualifierLookup(sport);
  };

  componentDidUpdate = async (prevProps: IProps) => {
    const {
      selectedSport, selectedLeague, selectedSchedule,
      sport,
      onClearTeams,
      onUpdateQualifierLookup,
    } = this.props;

    if (prevProps.selectedSchedule && selectedSchedule) {
      if (prevProps.selectedSchedule._id === selectedSchedule._id
        && (prevProps.selectedSport !== selectedSport || prevProps.selectedLeague !== selectedLeague)) {
        onClearTeams();
        await this.onGetGameId();
      }
    }

    // Qualifiers are sport-specific (hockey's come from the period lookup), so refetch
    // whenever the game being uploaded for moves to a different sport - including back
    // to the user's own, since state would still hold the other sport's values
    if (this.isUploadMode && sport && prevProps.sport !== sport) onUpdateQualifierLookup(sport);
  };

  onGetGameId = async (e: React.MouseEvent = null) => {
    const {
      selectedSeason,
      selectedSport,
      selectedLeague,
      onUpdateGameId,
    } = this.props;
    const { data: { data: { gameId: gId } } } = await getGameId(selectedSeason, selectedSport, selectedLeague);
    onUpdateGameId(gId);
  };

  handleGameDateChange = (value: Date) => {
    const { onUpdateGameDate } = this.props;
    const dateValid = (value) ? new Date(value) : null;
    onUpdateGameDate(moment(dateValid).startOf('day').utc().toISOString());
  };

  renderUploadForm = () => {
    const {
      phaseLabel,
      exchangeQualifierOptions,
      exchangeVideoAngleOptions,
      selectedUploadFiles,
      sport,
      setFileUploadQualifier,
      setFileUploadVideoAngle,
      setFileUploadComment,
      systemSettings,
      selectedSport,
      selectedLeague,
    } = this.props;

    if (!this.isUploadMode) return null;

    const filteredAngles = exchangeVideoAngleOptions.filter((option) => option.value !== ''); // Filter out empty values if needed
    return selectedUploadFiles.map((file, index) => (
      <FormRow key={index} className="upload-form-row">
        <FormGroup>
          <Label>
            File Name
            {file.videoCodec === VideoCodec.H265 && systemSettings?.AllowH265Uploads?.includes(`${selectedSport}:${selectedLeague}`) && (
              <Tooltip title={<>H265 may fail transcoding. We&apos;ll attempt it; the original file remains downloadable if it fails. <br />Consider uploading H264.</>} componentsProps={{ tooltip: { sx: { fontSize: '0.85rem' } } }}>
                <ReportProblemIcon
                  color="warning"
                  fontSize="small"
                  style={{ marginLeft: 4, verticalAlign: 'middle', cursor: 'pointer' }}
                />
              </Tooltip>
            )}
          </Label>
          <Tooltip title={file.filename} componentsProps={{ tooltip: { sx: { fontSize: '0.85rem' } } }}>
            <StyledTextField
              value={file.filename}
              fullWidth
              disabled
            />
          </Tooltip>
        </FormGroup>
        {!isLacrosse(sport) ? (
          <FormGroup>
            <Label>{phaseLabel}</Label>
            <Select
              menuPlacement="top"
              value={lookupToOption(file.qualifier, exchangeQualifierOptions)}
              options={exchangeQualifierOptions}
              onChange={(option: unknown) => setFileUploadQualifier(index, (option as ILookupOption)?.value)}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              // Lock only when the XML qualifier was actually applied. Keying off the
              // filename could leave a file with no qualifier and no way to set one,
              // which disables Create with nothing in the UI to fix.
              isDisabled={file.qualifier === XML_QUALIFIER}
            />
          </FormGroup>
        ) : (
          <FormGroup>
            <Label>Video Angle</Label>
            <Select
              menuPlacement="top"
              value={lookupToOption(file.videoAngle, filteredAngles)}
              options={filteredAngles}
              onChange={(option: unknown) => setFileUploadVideoAngle(index, (option as ILookupOption)?.value)}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            />
          </FormGroup>
        )}
        <FormGroup>
          <Label>Comment</Label>
          {/*
            VV-226: optional, and controlled rather than committed on blur so the character
            count, the error state and the disabled Create button all track what is typed.
            Committing on blur instead would let a paste-then-click get past the check.
          */}
          <StyledTextField
            value={file.comment || ''}
            placeholder="Optional"
            fullWidth
            multiline
            error={(file.comment || '').trim().length > EXCHANGE_COMMENT_MAX_LENGTH}
            helperText={(file.comment || '').trim().length > EXCHANGE_COMMENT_MAX_LENGTH
              ? `${(file.comment || '').trim().length} / ${EXCHANGE_COMMENT_MAX_LENGTH} characters - shorten the comment to upload`
              : ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileUploadComment(index, e.target.value)}
          />
        </FormGroup>

      </FormRow>
    ));
  };

  renderUploader = () => {
    const {
      isConference,
      uploader,
      scoutedTeamOption,
      teamOptions,
      onUpdateUploader,
      onUpdateScoutedTeam,
    } = this.props;

    if (!isConference) return null;

    return (
      <FormRow>
        <FormGroup>
          <Label>Uploader</Label>
          <Select
            value={uploader}
            options={teamOptions}
            onChange={(option: unknown) => onUpdateUploader(option as ITeamOption)}
            components={{
              Option: TeamOption,
              MenuList: VirtualizedMenuList,
            }}
          />
        </FormGroup>
        <FormGroup>
          <Label>Scouted Team</Label>
          <Select
            value={scoutedTeamOption}
            options={teamOptions}
            onChange={(option: unknown) => onUpdateScoutedTeam(option as ITeamOption)}
            components={{
              Option: TeamOption,
              MenuList: VirtualizedMenuList,
            }}
          />
        </FormGroup>
      </FormRow>
    );
  };

  renderSeasonAndGame = () => {
    const {
      selectedSeason, onSelectSeason, seasons,
      selectedSchedule, onSelectSchedule, exchangeSchedules,
      loggedInUsersTeam,
      isSeasonSelectable,
      sport,
      selectedSport,
      selectedLeague,
      teamIcons,
      sourceTeam,
    } = this.props;

    const shooterTeam = sourceTeam || loggedInUsersTeam;

    let schedules = exchangeSchedules
      .filter(({ Season }) => Season === selectedSeason)
      // eslint-disable-next-line
      .sort((a, b) => new Date(a.GameDate).getTime() > new Date(b.GameDate).getTime() ? 1 : -1);

    // We should be filtering in state
    if (selectedSport && selectedLeague) schedules = schedules.filter(({ Sport, League }) => Sport === selectedSport && League === selectedLeague);

    // note: hide inactive games when uploading
    if (this.isUploadMode) schedules = schedules.filter(({ IsActive }) => IsActive !== 'false');

    // Newest date first
    if (newerCalendersFirst(sport)) schedules = [...schedules.reverse()];

    const teamData: ITeamData = {
      teamIcons,
      source: sourceTeam,
    };

    // The season list was built at mount for the sport we had then. If the sport has
    // since moved to one that allows new games, make sure its season window is offered
    // too, otherwise there's no season to hang a newly created game off.
    const seasonOptions = this.allowNewGame
      ? Array.from(new Set([...seasons, ...newGameSeasons(sport)])).sort()
      : seasons;

    return (
      <FormRow>
        <FormGroup>
          <Label data-auto="SeasonSelectLabel">Season</Label>
          <Select
            value={selectedSeason ? valueToOption(selectedSeason, useCalendarSplit(sport)) : null}
            options={valuesToOptions(seasonOptions, useCalendarSplit(sport))}
            onChange={(option: unknown) => onSelectSeason((option as IValueOption)?.value)}
            isDisabled={!isSeasonSelectable}
          />
        </FormGroup>
        {(this.isUploadMode || this.isUpdateAssetsMode) && ( // Only for upload or update assets mode
          <FormGroup
            disabled={!selectedSeason}
          >
            <Label data-auto="GameSelectLabel">Game</Label>
            <Select
              value={selectedSchedule ? scheduleToOption(selectedSchedule, teamData) : null}
              options={schedulesToOptions(schedules, teamData, this.allowNewGame, shooterTeam, defaultGameType, selectedSeason)}
              onChange={(option: unknown) => onSelectSchedule((option as IScheduleOption)?.value)}
              isDisabled={!isSeasonSelectable || !selectedSeason}
              components={{
                Option: GameOption,
              }}
              data-auto="GameSelect"
            />
          </FormGroup>
        )}
      </FormRow>
    );
  };

  renderSportAndLeague = () => {
    const {
      selectedSport,
      onSelectSport,
      leagueOptions,
      selectedLeague,
      onSelectLeague,
      exchangeSportOptions,
      selectedSchedule,
      selectedSeason,
    } = this.props;

    if (!(this.isCreateMode || this.isUpdateMode)) return null;

    let isDisabled = false;
    if (this.isUpdateMode && !selectedSchedule) isDisabled = true;
    if (this.isCreateMode && !selectedSeason) isDisabled = true;
    return (
      <FormRow
        disabled={isDisabled}
      >
        <FormGroup>
          <Label data-auto="SportSelectLabel">Sport</Label>
          <Select
            value={lookupToOption(selectedSport, exchangeSportOptions)}
            options={exchangeSportOptions}
            onChange={(option: unknown) => onSelectSport((option as ILookupOption)?.value)}
            isDisabled={isDisabled}
          />
        </FormGroup>
        <FormGroup>
          <Label data-auto="LeagueSelectLabel">League</Label>
          <Select
            value={lookupToOption(selectedLeague, leagueOptions)}
            options={leagueOptions}
            onChange={(option: unknown) => onSelectLeague((option as ILookupOption)?.value)}
            isDisabled={isDisabled}
          />
        </FormGroup>
      </FormRow>
    );
  };

  renderGameType = () => {
    const {
      selectedExchangeGameType,
      exchangeGameTypeOptions,
      selectedSeason,
      selectedSchedule,
      onSelectExchangeGameType,
    } = this.props;

    if (!this.isCreateMode && !this.isUpdateMode && !(this.allowNewGame && this.checkNewGameSelected())) return <></>;

    let isDisabled = false;
    if (this.isUpdateMode && !selectedSchedule) isDisabled = true;
    if (this.isCreateMode && !selectedSeason) isDisabled = true;

    return (
      <FormRow
        disabled={isDisabled}
      >
        <FormGroup data-auto="GameTypeFormGroup">
          <Label data-auto="GameTypeSelectLabel">Game Type</Label>
          <Select
            value={lookupToOption(selectedExchangeGameType, exchangeGameTypeOptions)}
            options={exchangeGameTypeOptions}
            onChange={(option: unknown) => onSelectExchangeGameType((option as ILookupOption)?.value)}
            isDisabled={isDisabled}
          />
        </FormGroup>
      </FormRow>
    );
  };

  renderGameId = () => {
    const {
      homeGameNumber, onUpdateHomeGameNumber,
      gameId, onUpdateGameId,
      selectedSchedule, selectedSeason,
      selectedSport, selectedLeague,
      selectedExchangeGameType,
    } = this.props;

    let isDisabled = true;
    if (this.isUpdateMode) isDisabled = false;
    if (this.isCreateMode && selectedSeason && selectedSport && selectedExchangeGameType && selectedLeague) isDisabled = false;

    return (
      <>
        {!this.isCreateMode && !this.allowNewGame && (
          <FormRow disabled={!selectedSchedule}>
            <GameIdField
              isEditable={!isDisabled}
              gameId={gameId}
              selectedSeason={selectedSeason}
              selectedSport={selectedSport}
              selectedLeague={selectedLeague}
              onUpdateGameId={onUpdateGameId}
              onGetGameId={this.onGetGameId}
            />
            {this.isUploadMode && (
              <FormGroup>
                <Label>Game #</Label>
                <StyledTextField
                  type="number"
                  onChange={(e) => onUpdateHomeGameNumber(e.target.value)}
                  value={selectedSchedule ? homeGameNumber : 'N/A'}
                  disabled={!selectedSchedule}
                  fullWidth
                  data-auto="GameNumber"
                />
              </FormGroup>
            )}
          </FormRow>
        )}
        {this.isCreateMode && (
          <FormRow>
            <GameIdField
              isEditable={!isDisabled}
              gameId={gameId}
              selectedSeason={selectedSeason}
              selectedSport={selectedSport}
              selectedLeague={selectedLeague}
              onUpdateGameId={onUpdateGameId}
              onGetGameId={this.onGetGameId}
            />
          </FormRow>
        )}
      </>
    );
  };

  renderGameDate = () => {
    const {
      selectedSchedule,
      gameDate,
    } = this.props;

    const isGameDateSelectable = (this.isUpdateMode && !!selectedSchedule) || (this.isUploadMode && this.checkNewGameSelected()) || this.isCreateMode;

    return (
      <FormRow>
        <FormGroup
          disabled={!selectedSchedule}
        >
          <Label>Game Date</Label>
          <StyledDatePicker
            format="MM-dd-yyyy"
            value={parseDatefromString(gameDate)}
            onChange={(value) => this.handleGameDateChange(value as Date)}
            disabled={!isGameDateSelectable}
            data-auto="GameDatePicker"
            className="GameDatePicker"
            slotProps={{
              actionBar: {
                actions: ['clear', 'today'],
              },
            }}
          />
        </FormGroup>
      </FormRow>
    );
  };

  renderTeamRow = (teamType: 'Home' | 'Visitor') => {
    const {
      homeTeam,
      awayTeam,
      homeGameNumber,
      visitorGameNumber,
      strictTeamOptions,
      onUpdateHomeTeam,
      onUpdateAwayTeam,
      onUpdateHomeGameNumber,
      onUpdateVisitorGameNumber,
      selectedSchedule,
      sport,
      selectedSport,
      selectedSeason,
      selectedExchangeGameType,
      selectedLeague,
    } = this.props;

    let isTeamSelectable = false;
    if (this.isUploadMode && canUploadNewGame(sport) && this.checkNewGameSelected()) isTeamSelectable = true;
    if (this.isCreateMode && selectedSeason && selectedSport && selectedExchangeGameType && selectedLeague) isTeamSelectable = true;
    if (this.isUpdateMode && selectedSchedule && selectedSport) isTeamSelectable = true;

    const isHome = teamType === 'Home';
    const team = isHome ? homeTeam : awayTeam;
    const gameNumber = isHome ? homeGameNumber : visitorGameNumber;
    const onUpdateTeam = isHome ? onUpdateHomeTeam : onUpdateAwayTeam;
    const onUpdateGameNumber = isHome ? onUpdateHomeGameNumber : onUpdateVisitorGameNumber;

    return (
      <FormRow disabled={!isTeamSelectable}>
        {this.isUploadMode && !this.allowNewGame && (
          <FormGroup>
            <Label>{teamType} Team Code</Label>
            <StyledTextField
              value={team ? team.TeamCode : 'N/A'}
              disabled
              fullWidth
            />
          </FormGroup>
        )}
        <FormGroup data-auto={`${teamType}TeamFormGroup`}>
          <Label>{teamType} Team</Label>
          <Select
            menuPlacement="auto"
            // Must be null, never undefined: undefined makes react-select uncontrolled,
            // so it would keep displaying a picked team that never reached the store
            value={team ? teamToOption(team) : null}
            options={strictTeamOptions}
            onChange={(option: unknown) => onUpdateTeam(option as ITeamOption)}
            components={{
              Option: TeamOption,
              MenuList: VirtualizedMenuList,
            }}
            isDisabled={!isTeamSelectable}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
          />
        </FormGroup>
        {((this.isUploadMode && !this.checkNewGameSelected()) || this.isCreateMode) && (
          <FormGroup data-auto={`${teamType}TeamGameNumFormGroup`}>
            <Label>{teamType} Team Game #</Label>
            <StyledTextField
              type="number"
              onChange={(e) => onUpdateGameNumber(e.target.value)}
              value={gameNumber || 'N/A'}
              disabled
              fullWidth
            />
          </FormGroup>
        )}
      </FormRow>
    );
  };

  renderPhaseAndActive = () => {
    const {
      selectedSchedule,
      selectedExchangeVideoAngle,
      exchangeVideoAngleOptions,
      onSelectExchangeVideoAngle,
      sport,
      isActive,
      onToggleIsActive,
    } = this.props;

    if (this.isUploadMode) return null;

    return (
      <FormRow disabled={!selectedSchedule}>
        <FormGroup>
          {this.isUpdateMode && (
            <FormControlLabel
              label="Game Active"
              control={(
                <Switch
                  disabled={!this.isCreateMode && !selectedSchedule}
                  checked={isActive}
                  onChange={onToggleIsActive}
                  name="checkedB"
                  color="primary"
                  data-auto="GameActiveSwitch"
                />
              )}
            />
          )}
        </FormGroup>
        {(this.isUploadMode && isSoccer(sport)) && (
          <FormGroup>
            <Label>Video Angle</Label>
            <Select
              menuPlacement="top"
              value={lookupToOption(selectedExchangeVideoAngle, exchangeVideoAngleOptions)}
              options={exchangeVideoAngleOptions}
              onChange={(option: unknown) => onSelectExchangeVideoAngle((option as ILookupOption)?.value)}
              isDisabled={!selectedSchedule}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            />
          </FormGroup>
        )}
      </FormRow>
    );
  };

  renderShooterTeam = () => {
    const { sourceTeam, isConference } = this.props;
    if (!this.isUploadMode && !isConference) return null;
    return (
      <FormRow disabled>
        {!this.allowNewGame && (
          <FormGroup>
            <Label>Shooter Team Code</Label>
            <StyledTextField
              value={sourceTeam ? sourceTeam.TeamCode : 'N/A'}
              disabled
              fullWidth
            />
          </FormGroup>
        )}
        <FormGroup>
          <Label>Shooter Team</Label>
          <StyledTextField
            value={sourceTeam ? sourceTeam.TeamName : 'N/A'}
            disabled
            fullWidth
          />
        </FormGroup>
      </FormRow>
    );
  };

  renderForm = () => {
    const { formHeading } = this.props;

    return (
      <Form>
        {formHeading}
        {this.isUpdateMode && <Filters />}
        {this.renderUploader()}
        {!this.isUpdateMode && this.renderSeasonAndGame()}
        {this.renderSportAndLeague()}
        {this.renderGameType()}
        {this.renderGameId()}
        {this.renderGameDate()}
        {this.renderTeamRow('Home')}
        {this.renderTeamRow('Visitor')}
        {this.renderUploadForm()}
        {this.renderPhaseAndActive()}
        {this.renderShooterTeam()}
      </Form>
    );
  };

  render = () => {
    const {
      isConference,
      isLoaded,
      isError,
      exchangeSchedules,
      sport,
    } = this.props;

    const canRender = (this.isCreateMode || canUploadNewGame(sport)) ? true : exchangeSchedules.length !== 0;
    return (
      <>
        <ProgressContainer>
          {!isLoaded && <LinearProgress />}
        </ProgressContainer>
        <DialogContentContainer>
          {isLoaded && (!canRender || isError) && (
            <WarningContainer>
              <h1>No seasons or games found</h1>
            </WarningContainer>
          )}
          {isLoaded && (isConference || canRender) && this.renderForm()}
        </DialogContentContainer>
      </>
    );
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(GameScheduleForm);
